import { SEARCHER_OPTIONS, SEARCHER_TYPES } from '@core/constants/SEARCHER';
import { getAiFromUrl, getSearchOptionUrl } from './searcher';
import { isCreativeModeFromPath, isEditorialModeFromPath, openLink } from './utils';
import { generateCode, generateUUID } from './uuid';
import {
    addNewSearcherHistoryElement,
    getWesogUserInfo,
    updateWesogUserInfo,
} from './wesog-user';
import { addStoreFiltersState, getStoreFilterStateById, getStoreValue } from './store';
import { MAPPING_ROUTES, ROUTES } from '@core/constants/ROUTES';
import { PROV_FILTERS } from '@core/constants/API';
import { WESOG_SESSION_STORE_PARAMS, WESOG_STORE_PARAMS } from '@core/constants/STORE';
import { updateSessionStoreValue } from './sessionStore';

/**
 * Go to the similar images query.
 * @param {string} imageId - Image id to go to the similar images query.
 * @param {string} [provider] - Provider to go to the similar images query. Default: gtres.
 * @param {string} imageURL - Image url to go to the similar images query.
 * @returns {void}
 */
export const goToSimilarImagesQuery = (imageId, provider, imageURL) => {
    /** @type {URLSearchParams} */
    const urlParams = new URLSearchParams(window.location.search);
    /** @type {string} */
    let searchOptions = getSearchOptionUrl(urlParams);
    searchOptions = searchOptions.includes(SEARCHER_OPTIONS.ED_VIDEOS)
        ? SEARCHER_OPTIONS.ED_IMGS
        : searchOptions;
    /** @type {string} */
    const uuid4 = generateUUID();
    /** @type {{ username: string, access_token: string, searcherHistory: string, lastName: string, email: string, id: string, language: string, phone: string, pseudo: string}} */
    const user = getWesogUserInfo();
    /** @type {string} */
    const searcherIdCode = generateCode();

    user.searcherHistory = addNewSearcherHistoryElement(user.searcherHistory, {
        [SEARCHER_TYPES.IMAGE_ID]: uuid4,
        [SEARCHER_TYPES.IMAGE_URLS]: [imageURL],
        [SEARCHER_TYPES.AI]: true,
        [SEARCHER_TYPES.OPTION]: searchOptions,
        [SEARCHER_TYPES.TEXT]: urlParams.get(SEARCHER_TYPES.TEXT) || '',
    });
    updateWesogUserInfo(user);

    addStoreFiltersState(searcherIdCode);

    const /** @type {string} */ newUrl = `${ROUTES.RESULTS}?${
            SEARCHER_TYPES.IMG_UID
        }=${imageId}&${SEARCHER_TYPES.OPTION}=${searchOptions}&${
            SEARCHER_TYPES.AI
        }=true&${SEARCHER_TYPES.PROVIDER}=${provider || PROV_FILTERS.GTRES}&${
            SEARCHER_TYPES.IMAGE_ID
        }=${uuid4}&${SEARCHER_TYPES.SEARCHER_ID}=${searcherIdCode}`;

    openLink(newUrl);
};

/**
 * Go to the home view.
 * @returns {void}
 */
export const goToHomeView = () => {
    /** @type {URLSearchParams} */
    const currentUrlParams = new URLSearchParams(window.location.search);
    /** @type {string | undefined} */
    const defaultHomeReportView = getStoreValue(
        WESOG_STORE_PARAMS.DEFAULT_HOME_REPORT_VIEW,
    );
    /** @type {string} */
    let newUrl = '';

    if (defaultHomeReportView) {
        const [homeReportView, homeReportViewParams] = defaultHomeReportView.split('?');
        const /** @type {URLSearchParams} */ defaultHomeReportViewParams =
                new URLSearchParams(`?${homeReportViewParams}`);
        const /** @type {string} */ searchId = defaultHomeReportViewParams.has(
                SEARCHER_TYPES.SEARCHER_ID,
            )
                ? defaultHomeReportViewParams.get(SEARCHER_TYPES.SEARCHER_ID)
                : generateCode();

        /** Will create a new search id if we have nothing  */
        if (!getStoreFilterStateById(searchId)) {
            addStoreFiltersState(searchId);
        }

        defaultHomeReportViewParams.set(SEARCHER_TYPES.SEARCHER_ID, searchId);

        newUrl = `${homeReportView}?${defaultHomeReportViewParams.toString()}`;
    } else {
        /** @type {string | null} */
        const lastVisitedNavLink = getStoreValue(
            WESOG_STORE_PARAMS.NAV_LINKS_LAST_VISITED,
        );

        if (lastVisitedNavLink) {
            /* check if the current page is the last visited nav link */
            if (window.location.href === lastVisitedNavLink) {
                window.location.reload();
            }

            const [homeLastVisitedView, homeLastVisitedParams] =
                lastVisitedNavLink.split('?');
            /** @type {URLSearchParams} */
            const homeLastVisitedParamsUrl = new URLSearchParams(
                `?${homeLastVisitedParams}`,
            );
            homeLastVisitedParamsUrl.set(
                SEARCHER_TYPES.AI,
                homeLastVisitedParams.includes(SEARCHER_OPTIONS.ED_IMGS)
                    ? 'false'
                    : getAiFromUrl(currentUrlParams),
            );

            newUrl = `${homeLastVisitedView}?${homeLastVisitedParamsUrl.toString()}`;
        } else {
            newUrl = `${ROUTES.HOME}?${SEARCHER_TYPES.OPTION}=${getSearchOptionUrl(
                currentUrlParams,
            )}&${SEARCHER_TYPES.AI}=false`;
        }
    }

    openLink(newUrl);
};

/**
 * Go to the details view or report details.
 * @param {object} report - Object with the report data.
 * @returns {void}
 */
export const goToDetailsViewOrReportDetails = (report) => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );
    const /** @type {string} */ searchOptions = getSearchOptionUrl(urlParams);
    let /** @type {boolean} */ isOnlyOneElement =
            !report.defaultItem || (report.defaultItem && report.elements === 1);
    const /** @type {string} */ searcherIdCode = generateCode();

    if (isOnlyOneElement) {
        updateSessionStoreValue(
            WESOG_SESSION_STORE_PARAMS.CURRENT_SELECTED_IMAGE,
            report?.defaultItem || report,
        );
    } else {
        addStoreFiltersState(searcherIdCode);
    }

    const /** @type {string} */ url = isOnlyOneElement
            ? `${ROUTES.DETAILS}?${
                  SEARCHER_TYPES.IMG_UID
              }=${report?.defaultItem?.uid || report?.uid}&${SEARCHER_TYPES.OPTION}=${searchOptions}&${
                  SEARCHER_TYPES.AI
              }=${getAiFromUrl(urlParams)}`
            : `${ROUTES.REPORT_DETAILS}?${SEARCHER_TYPES.REPORT_ID}=${report.uid}&${
                  SEARCHER_TYPES.OPTION
              }=${searchOptions}&${SEARCHER_TYPES.AI}=${getAiFromUrl(urlParams)}&${
                  SEARCHER_TYPES.SEARCHER_ID
              }=${searcherIdCode}`;

    openLink(url);
};

/**
 * Go to the search results view.
 * @param {URLSearchParams} newUrlParams - New URL params to go to the search results view.
 * @returns {void}
 */
export const goToSearchResultsView = (newUrlParams) => {
    const /** @type {URLSearchParams} */ currentUrlParams = new URLSearchParams(
            window.location.search,
        );
    const /** @type {string} */ searchOptions = getSearchOptionUrl(currentUrlParams);
    const /** @type {URLSearchParams} */ searcherIdCode = generateCode();

    addStoreFiltersState(searcherIdCode);

    const /** @type {string} */ newUrl = `${ROUTES.RESULTS}?${newUrlParams.toString()}&${
            SEARCHER_TYPES.OPTION
        }=${searchOptions}&${SEARCHER_TYPES.AI}=${getAiFromUrl(currentUrlParams)}&${
            SEARCHER_TYPES.SEARCHER_ID
        }=${searcherIdCode}`;

    openLink(newUrl);
};

/**
 * Go out from the private views.
 * @returns {void}
 */
export const goOutPrivateViews = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    if (
        window.location.pathname.includes(ROUTES.LIGHT_BOX) ||
        window.location.pathname.includes(ROUTES.DOWNLOAD_LIGHTBOX) ||
        window.location.pathname.includes(ROUTES.CART_LIST) ||
        window.location.pathname.includes(ROUTES.USER_PROFILE) ||
        (window.location.pathname.includes(ROUTES.REPORT_DETAILS) &&
            urlParams.has(SEARCHER_TYPES.LIGHTBOX_ID))
    ) {
        goToHomeView();
    }
};

/**
 * Go to the details view.
 * @param {string} uid - The uid of the element.
 * @returns {void}
 */
export const goToDetailsView = (uid) => {
    /** @type {URLSearchParams} */
    const urlParams = new URLSearchParams(window.location.search);
    /** @type {string} */
    const searchOptions = getSearchOptionUrl(urlParams);

    const /** @type {string} */ newUrl = `${ROUTES.DETAILS}?${
            SEARCHER_TYPES.IMG_UID
        }=${uid}&${SEARCHER_TYPES.OPTION}=${searchOptions}&${
            SEARCHER_TYPES.AI
        }=${getAiFromUrl(urlParams)}`;

    openLink(newUrl);
};

/**
 * Go to the report details page.
 * @param {string} idEvent - The id event.
 * @returns {void}
 */
export const goToReportDetails = (idEvent) => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );
    const /** @type {string} */ searcherIdCode = generateCode();

    addStoreFiltersState(searcherIdCode);

    const /** @type {string} */ newUrl = `${ROUTES.REPORT_DETAILS}?${
            SEARCHER_TYPES.REPORT_ID
        }=${idEvent}&${SEARCHER_TYPES.OPTION}=${getSearchOptionUrl(urlParams)}&${
            SEARCHER_TYPES.AI
        }=${getAiFromUrl(urlParams)}&${SEARCHER_TYPES.SEARCHER_ID}=${searcherIdCode}`;

    openLink(newUrl);
};

/**
 * Redirect to the new routes.
 * @returns {void}
 */
export const redirectOldRoutes = () => {
    const /** @type {URL} */ url = new URL(window.location.href);
    const /** @type {String} */ path = url.pathname;

    if (MAPPING_ROUTES[path]) {
        window.location.replace(MAPPING_ROUTES[path] + window.location.search);
    }
};

/**
 * Check if the home URL is correct with the search option.
 * @returns {void}
 */
export const checkHomeUrlWithSearchOption = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );
    const /** @type {string} */ searchOption =
            urlParams.get(SEARCHER_TYPES.OPTION) || SEARCHER_OPTIONS.ED_IMGS;

    if (isCreativeModeFromPath()) {
        if (searchOption !== SEARCHER_OPTIONS.CR_IMGS) {
            urlParams.set(SEARCHER_TYPES.OPTION, SEARCHER_OPTIONS.CR_IMGS);
            window.location.replace(`${ROUTES.HOME_CREATIVE}?${urlParams.toString()}`);
        }
    }

    if (isEditorialModeFromPath()) {
        if (searchOption !== SEARCHER_OPTIONS.ED_IMGS) {
            urlParams.set(SEARCHER_TYPES.OPTION, SEARCHER_OPTIONS.ED_IMGS);
            window.location.replace(`${ROUTES.HOME_EDITORIAL}?${urlParams.toString()}`);
        }
    }
};
